import { createContext, useContext, ReactNode } from "react";

import { useOrganizationMe } from "@/hooks/use-organization";
import type { FeatureKey } from "@/constants/permissions";
import type {
  Organization,
  Membership,
  PendingInvitation,
} from "@/services/organization.service";

type OrganizationContextType = {
  organization: Organization | null;
  membership: Membership | null;
  pendingInvitation: PendingInvitation | null;
  graceMode: boolean;
  provisioningFailed: boolean;
  isLoading: boolean;
  /** True when the current member has access to the given feature. */
  can: (feature: FeatureKey) => boolean;
  refresh: () => void;
};

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const OrganizationProvider = ({ children }: { children: ReactNode }) => {
  const { me, isLoading, mutate } = useOrganizationMe();

  const can = (feature: FeatureKey): boolean => {
    if (isLoading) return false;
    // Rollout grace mode: pre-backfill accounts keep full access.
    if (me?.graceMode) return true;
    return me?.permissions?.[feature] ?? false;
  };

  const value: OrganizationContextType = {
    organization: me?.organization ?? null,
    membership: me?.membership ?? null,
    pendingInvitation: me?.pendingInvitation ?? null,
    graceMode: me?.graceMode ?? false,
    provisioningFailed: me?.provisioningFailed ?? false,
    isLoading,
    can,
    refresh: () => void mutate(),
  };

  return (
    <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>
  );
};

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error("useOrganization must be used within an OrganizationProvider");
  }
  return context;
};
